import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays } from "lucide-react";

import api from "../../lib/api";
import { Loading } from "../../components/shared";

export default function ResidentMeetings() {
  const [meetings, setMeetings] = useState(null);

  useEffect(() => {
    api
      .get("/resident-meetings/meetings")
      .then((response) => setMeetings(response.data))
      .catch(() => setMeetings([]));
  }, []);

  if (!meetings) return <Loading />;
  const currentTime = Date.now();
  const upcoming = meetings.filter(
    (meeting) => Date.parse(meeting.scheduled_at) >= currentTime,
  );
  const past = meetings.filter(
    (meeting) => Date.parse(meeting.scheduled_at) < currentTime,
  );
  const renderMeetings = (items, testId, emptyText) => (
    <div className="mt-3 space-y-3" data-testid={testId}>
      {items.length ? (
        items.map((meeting) => (
          <Link
            className={
              "block border border-slate-200 bg-white p-5 transition-colors " +
              "hover:border-blue-500 hover:bg-blue-50"
            }
            data-testid={`resident-meeting-${meeting.meeting_id}`}
            key={meeting.meeting_id}
            to={`/resident/meetings/${meeting.meeting_id}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold text-blue-700">
                  {meeting.meeting_type.replaceAll("_", " ")} ·{" "}
                  {meeting.meeting_number}
                </p>
                <h2 className="mt-2 font-display text-xl font-semibold text-slate-900">
                  {meeting.title}
                </h2>
                <p className="mt-2 text-sm text-slate-600">
                  {meeting.location || "Location to be confirmed"}
                </p>
              </div>
              <p className="text-xs text-slate-500">
                {meeting.scheduled_at.replace("T", " ").slice(0, 16)}
              </p>
            </div>
            <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-600">
              <span>{meeting.agenda_items.length} agenda item(s)</span>
              {meeting.rsvp_status && <span>RSVP: {meeting.rsvp_status}</span>}
              {meeting.has_published_minutes && (
                <span className="font-semibold text-emerald-700">
                  Minutes published
                </span>
              )}
            </div>
          </Link>
        ))
      ) : (
        <p className="border border-dashed border-slate-300 bg-white p-5 text-sm text-slate-600">
          {emptyText}
        </p>
      )}
    </div>
  );

  return (
    <div data-testid="resident-meetings-page">
      <header className="border-b border-slate-200 pb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          Society governance
        </p>
        <h1 className="mt-2 font-display text-4xl font-bold text-slate-900">
          Meetings
        </h1>
      </header>
      <section className="mt-6" data-testid="resident-meeting-list">
        {meetings.length ? (
          <>
            <h2 className="font-display text-2xl font-semibold">
              Upcoming meetings
            </h2>
            {renderMeetings(
              upcoming,
              "resident-upcoming-meetings",
              "No upcoming meetings are available.",
            )}
            <h2 className="mt-8 font-display text-2xl font-semibold">
              Past meetings
            </h2>
            {renderMeetings(
              past,
              "resident-past-meetings",
              "No past meetings are available.",
            )}
          </>
        ) : (
          <div
            className="border border-dashed border-slate-300 bg-white p-7 text-sm text-slate-600"
            data-testid="resident-meeting-empty"
          >
            <CalendarDays className="mb-3 h-5 w-5 text-slate-500" />
            No published meetings are available for your current Society and
            home access.
          </div>
        )}
      </section>
    </div>
  );
}
